import {RootState} from "state/store";
import {api as baseAPI} from "../api";
import {
    BaseQueryApi,
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    createApi,
    fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import {setMcatChatAuthInfo, setShowMcatChatErrorModal} from "../../state/reducers/AuthSlice";
import {mcatChatExternal} from "./mcatChatExternal";

// handle request queue for 401 errors
let isRefreshing = false;
let failedRequestsQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
}> = [];

// this use to get the token of stored in redux layer. it take a moment to store there, until that this will
// request it
const waitForReduxStoredTokenAvailability = (getState: any) => {
    return new Promise((resolve, reject) => {
        let checkCount = 0;
        const checkToken = () => {
            const token = (getState() as RootState).auth.mcatChatAuthInfo.mcatChatToken;
            if (token) {
                resolve(token)
            } else {
                checkCount = checkCount + 1;
                if (checkCount > 10) {
                    reject();
                } else {
                    setTimeout(checkToken, 100)
                }
            }
        }
        checkToken();
    })
};

// base query which defined the basic configurations
const baseQuery = fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_MCAT_CHAT_BASE_API}`,

    prepareHeaders: async (headers, { getState }) => {
        try {
            const token = await waitForReduxStoredTokenAvailability(getState);
            if (token) {
                headers.set("Authorization", `Bearer ${token}`);
                return headers;
            }
        } catch (err: any) { }

    },
});

// wrapper API which implemented for 401 errors
const baseQueryWithReAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: {}
) => {
    let result = await baseQuery(args, api, extraOptions);

    if (result.error) {

        const isAuthEndpoint = typeof args !== 'string' &&
            args.url.match(/\/Authenticate/);

        if (result.error?.status === 401 && !isAuthEndpoint) {
            // Check if we're already refreshing
            if (!isRefreshing) {
                isRefreshing = true;

                try {
                    const refreshResult = await api.dispatch(
                        mcatChatExternal.endpoints.authenticateUser.initiate({})
                    );

                    if (refreshResult.data) {
                        const newToken = refreshResult?.data?.jwtAccessToken;
                        // Resolve all queued requests with new token
                        failedRequestsQueue.forEach(({ resolve }) => resolve(newToken));
                    } else {
                        // Reject all queued requests
                        failedRequestsQueue.forEach(({ reject }) => reject(new Error('Failed to refresh token')));

                        // logout or finish the process
                        api.dispatch(setMcatChatAuthInfo({ isMcatChatSessionEnd: true }));
                        api.dispatch(setShowMcatChatErrorModal(true));
                    }
                } finally {
                    isRefreshing = false;
                    failedRequestsQueue = [];
                }

                // Retry the original request with new token
                result = await baseQuery(args, api, extraOptions);
            } else {
                // If refreshing is in progress, add to queue
                return new Promise((resolve, reject) => {
                    failedRequestsQueue.push({
                        resolve: async (newToken) => {
                            // Update headers with new token
                            if (typeof args === 'string') {
                                args = {
                                    url: args,
                                    headers: { Authorization: `Bearer ${newToken}` }
                                };
                            } else {
                                args.headers = {
                                    ...args.headers,
                                    Authorization: `Bearer ${newToken}`
                                };
                            }
                            // Retry original request
                            const retryResult = await baseQuery(args, api, extraOptions);
                            resolve(retryResult);
                        },
                        reject
                    });
                });
            }
        } else {
            api.dispatch(setMcatChatAuthInfo({ isMcatChatSessionEnd: true }));
            api.dispatch(setShowMcatChatErrorModal(true));
        }
    }
    return result;
};

// this is the base API exported to access. for the injection process we can use the same
export const mcatChatApi = createApi({
    reducerPath: "mcatChatApi",
    tagTypes: [
        "Subjects",
    ],
    baseQuery: baseQueryWithReAuth,
    endpoints: () => ({}),
});
